import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BarChart3, Target, Users, MapPin, Calendar } from 'lucide-react';
import { useCollegeContext } from '../../contexts/CollegeContext';

const CollegeComparison = () => {
  const { colleges, COLLEGE_CATEGORIES } = useCollegeContext();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-0 py-0">
          <div className="flex items-center gap-4">
            <Link 
              to="/college"
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-lg font-bold text-slate-900">College comparison</h1>
              <p className="text-slate-600 mt-1">Compare and strategize across your college portfolio</p>
            </div>
          </div>
        </div>
      </div>
 
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-0 py-0">
        {colleges.length > 0 ? (
          <div className="space-y-8">
            {/* Portfolio Overview */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Portfolio Balance</h2>
              <div className="grid grid-cols-3 gap-6">
                {Object.entries(COLLEGE_CATEGORIES).map(([key, category]) => {
                  const categoryColleges = colleges.filter(c => c.category === key);
                  return (
                    <div key={key} className={`${
                      key === 'reach' ? 'bg-red-50 border-red-200' :
                      key === 'target' ? 'bg-azure-50 border-azure-200' :
                      'bg-emerald-50 border-emerald-200'
                    } border rounded-lg p-4`}>
                      <div className={`text-2xl font-bold mb-1 ${
                        key === 'reach' ? 'text-red-600' :
                        key === 'target' ? 'text-azure-600' :
                        'text-emerald-600'
                      }`}>
                        {categoryColleges.length}
                      </div>
                      <div className={`text-sm font-medium mb-1 ${
                        key === 'reach' ? 'text-red-700' :
                        key === 'target' ? 'text-azure-700' :
                        'text-emerald-700'
                      }`}>
                        {category.label}
                      </div>
                      <div className={`text-xs ${
                        key === 'reach' ? 'text-red-600' :
                        key === 'target' ? 'text-azure-600' :
                        'text-emerald-600'
                      }`}>
                        {category.description}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Colleges Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">College Comparison</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-3 px-6 text-sm font-medium text-slate-700">College</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-slate-700">Category</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-slate-700">Application Round</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-slate-700">Status</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-slate-700">Strategy</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {colleges.map(college => (
                      <tr key={college.id} className="hover:bg-slate-50">
                        <td className="py-4 px-6">
                          <div>
                            <div className="font-medium text-slate-900">{college.name}</div>
                            <div className="text-sm text-slate-600 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {college.location}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            college.category === 'reach' ? 'bg-red-100 text-red-700' :
                            college.category === 'target' ? 'bg-azure-100 text-azure-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {COLLEGE_CATEGORIES[college.category]?.label}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="text-sm text-slate-700">
                            {college.application_round?.replace('_', ' ') || 'Not set'}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            college.application_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            college.application_status === 'submitted' ? 'bg-azure-100 text-azure-700' :
                            college.application_status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {college.application_status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <Link 
                            to={`/college/${college.id}`}
                            className="text-azure-600 hover:text-azure-700 text-sm font-medium"
                          >
                            View Strategy →
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-400" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Colleges to Compare</h3>
            <p className="text-slate-600 mb-6">Add colleges to your portfolio to start college comparison</p>
            <Link 
              to="/college"
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
            >
              Add Colleges
            </Link>
          </div>
        )}
      </div>
    </div> 
  );
};

export default CollegeComparison;